import { useEffect, useState } from 'react';
import './join-league.styles.scss';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { phaseLabel, recordText } from '@/utils/leagueLabels';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import { top25From } from '@/utils/top25';
import { leagueTheme, useShellTheme } from '@/utils/theme';
import Modal, { ModalAlert } from '@/components/modal/modal.component';
import { hometownText } from '@shared/places.js';
import { conferencePrestige, prestigeStars } from '@shared/prestige.js';
import PrestigeStars from '@/components/SHARED/prestige-stars/prestige-stars.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

// Pick a team to manage in one league. leagues: {id: league document}.
const JoinLeague = ({ leagues, appId }) => {

    const { leaguePath } = useParams();
    const league = leagues && Object.values(leagues).find(l => l.path === leaguePath);
    const [ teams, setTeams ] = useState(null);
    const [ rankings, setRankings ] = useState(null);
    // Preview the league's look while choosing a team.
    useShellTheme(leagueTheme(league));
    const [ conference, setConference ] = useState('all');
    const [ openOnly, setOpenOnly ] = useState(true);
    const [ choice, setChoice ] = useState(null);
    const [ joining, setJoining ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const navigate = useNavigate();

    const leagueId = league?.id;
    useEffect(() => {
        if (!leagueId) return;
        let unsub;
        let unsubRanks;
        listenToDoc({
            'path': `leagues/${leagueId}/summary/teams`,
            'callback': (d) => setTeams(d?.teams ?? {}),
            'handleListener': (u) => { unsub = u; },
        });
        listenToDoc({
            'path': `leagues/${leagueId}/summary/rankings`,
            'callback': setRankings,
            'handleListener': (u) => { unsubRanks = u; },
        });
        return () => {
            if (unsub) unsub();
            if (unsubRanks) unsubRanks();
        }
    }, [leagueId])

    if (!leagues) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (!league) return <p className='meta'>League not found. <Link to='/leagues'>Back to leagues</Link></p>;

    const back = <Link to='/leagues' className='back-link'><FontAwesomeIcon icon={faChevronLeft} /> Leagues</Link>;
    const myTeamId = league.managers?.[appId];
    if (myTeamId) {
        return (
            <div className='join-league'>
                {back}
                <div className='g-card'>
                    <h2>{league.name}</h2>
                    <p>You already manage {teams?.[myTeamId]?.name ?? 'a team'} in this league.</p>
                    <button className='g-button primary' onClick={() => navigate(`/home/${league.id}`)}>Go to my team</button>
                </div>
            </div>
        )
    }

    const conferences = Object.values(league.conferences).sort((a, b) => a.name.localeCompare(b.name));
    const list = teams && Object.values(teams)
        .filter(t => conference === 'all' || t.conference === conference)
        .filter(t => !openOnly || !t.managerId)
        .sort((a, b) => a.name.localeCompare(b.name));
    const college = league.type !== 'pro';
    const top25 = top25From(league, rankings);
    const confPrestige = college && teams ? conferencePrestige(Object.values(teams)) : {};

    async function join() {
        setJoining(true);
        const res = await UseCloudFunction('joinTeam', { leagueId: league.id, teamId: choice.id });
        setJoining(false);
        if (res.error) {
            setChoice(null);
            setAlertMessage(res.error);
            return;
        }
        navigate(`/home/${league.id}`);
    }

    return (
        <div className='join-league'>
            {back}
            <div className='g-card join-head'>
                <h2>{league.name}</h2>
                <div className='meta'>
                    {college ? 'College' : 'Pro'} · {league.settings.teamCount} teams · {phaseLabel(league.season, league.type)}
                </div>
                <p>
                    Choose a team to manage. You set its lineup; the games are played for you on the league&apos;s schedule.
                    Teams without a manager run themselves.
                </p>
                <div className='join-filters'>
                    <div className='field'>
                        <label htmlFor='conf-filter'>Conference</label>
                        <select id='conf-filter' value={conference} onChange={(e) => setConference(e.target.value)}>
                            <option value='all'>All conferences</option>
                            {conferences.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.name}{confPrestige[c.id] ? ` · ${prestigeStars(confPrestige[c.id])}★` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    <label className='open-only'>
                        <input type='checkbox' checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
                        Open teams only
                    </label>
                </div>
            </div>

            {!list ? <div className='center-col'><ThreeDotsLoader /></div> :
                list.length === 0 ? <div className='g-card'><p className='meta'>No teams match.</p></div> :
                <div className='team-choices'>
                    {list.map(t => (
                        <button
                            key={t.id}
                            className='g-card team-choice'
                            disabled={Boolean(t.managerId)}
                            onClick={() => setChoice(t)}>
                            <TeamBadge team={t} size='md' />
                            <span className='team-choice-text'>
                                <span className='team-choice-name'>
                                    {t.name} <span className='mascot'>{t.mascot}</span>
                                    {top25[t.id] && <span className='top25-rank'> ({top25[t.id]})</span>}
                                </span>
                                <span className='meta'>{[hometownText(t, true), league.conferences[t.conference]?.name].filter(Boolean).join(' · ')}</span>
                                <span className='team-choice-stats'>
                                    {college && t.prestige ? <PrestigeStars value={t.prestige} label='Program prestige' /> : null}
                                    <span>{recordText(t.record)}</span>
                                    <span>Chemistry {t.chemistryGrade?.grade ?? '-'}</span>
                                </span>
                                {t.managerId && <span className='taken'>Managed by {t.managerName ?? 'another user'}</span>}
                            </span>
                        </button>
                    ))}
                </div>
            }

            <Modal show={Boolean(choice)} cancel={() => setChoice(null)} text='Manage this team?' closeButton={true} cls='narrow'>
                {choice &&
                    <div className='modal-children confirm-join'>
                        <div className='confirm-team'>
                            <TeamBadge team={choice} size='lg' />
                            <div>
                                <b>{choice.name} {choice.mascot}</b>
                                <div className='meta'>{league.conferences[choice.conference]?.name} · {recordText(choice.record)}</div>
                            </div>
                        </div>
                        <p>You can manage one team in this league. You can give it up later from your team page.</p>
                        <div className='buttons'>
                            <button className='g-button' onClick={() => setChoice(null)}>Cancel</button>
                            <button className={`g-button primary ${joining ? 'disabled' : ''}`} disabled={joining} onClick={join}>
                                {joining ? <ThreeDotsLoader /> : 'Manage this team'}
                            </button>
                        </div>
                    </div>
                }
            </Modal>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

export default JoinLeague
